/**
 * apply-category-presets.ts
 *
 * Pure function that seeds a NEW job's V3 content from a category's presets at
 * creation time (Plan 4, Task 1 — Spec A §4). The copy is made once at job
 * creation and becomes job-owned; it is NOT a live cascade from the category.
 *
 * defaultRoleIds is handled by a separate server step and is intentionally
 * ignored here — it must not appear in the returned content.
 */
import { PIPELINE_TEMPLATES } from '../types/pipeline-templates.js';
import { REQUIRED_STAGES, CANONICAL_STAGES } from './profiles.js';
// ---------------------------------------------------------------------------
// Step-type → Stage mapping (mirrors snapshot-builder.ts mapLegacyStepToStage)
// ---------------------------------------------------------------------------
const STEP_TO_STAGE = {
    scan: 'import',
    classify: 'form-recognition',
    key: 'key-entry',
    verify: 'verification',
    qc: 'qa',
    supervisor: 'supervisor',
    export: 'export',
};
// ---------------------------------------------------------------------------
// Pre-compute content Stage[] for each known pipeline template at module load
// time, using the same union-then-sort logic as snapshot-builder.ts.
// Source: PIPELINE_TEMPLATES (pipeline-templates.ts) + REQUIRED_STAGES +
// CANONICAL_STAGES (profiles.ts).
// ---------------------------------------------------------------------------
function computeTemplateStages(templateId) {
    const template = PIPELINE_TEMPLATES[templateId];
    if (!template)
        return [];
    const mapped = template.steps
        .map((s) => STEP_TO_STAGE[s.stepType])
        .filter((s) => s !== undefined);
    const stageSet = new Set([...REQUIRED_STAGES, ...mapped]);
    return CANONICAL_STAGES.filter((s) => stageSet.has(s));
}
/** Content-stage arrays keyed by pipeline template id, computed once at load time. */
const CONTENT_STAGE_LISTS = Object.fromEntries(Object.keys(PIPELINE_TEMPLATES).map((id) => [id, computeTemplateStages(id)]));
// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
/**
 * Seed a NEW job's V3 content from a category's presets (Spec A §4 — copied
 * once at creation, then job-owned; NOT a live cascade). Pure + immutable:
 * returns a new content object, never mutates the input.
 *
 * - `defaultDestinations`: appended to `content.exportDestinations` with
 *   deterministic ids (`preset_dest_${i}` where i is the preset array index).
 *   These ids are stable across calls for test-fixture predictability; the
 *   create-handler / destination-sync is responsible for re-keying to real
 *   cuids on persist if needed.
 * - `defaultPipelineTemplate`: maps to a Stage[] computed from the shared
 *   PIPELINE_TEMPLATES definition using the same step→stage mapping as the
 *   snapshot-builder (REQUIRED_STAGES union + CANONICAL_STAGES sort).
 * - `defaultRoleIds`: IGNORED — handled by the server layer, not content.
 */
export function applyCategoryPresets(presets, content) {
    let next = content;
    // ── Destinations ──────────────────────────────────────────────────────────
    if (presets.defaultDestinations && presets.defaultDestinations.length > 0) {
        const seededDests = presets.defaultDestinations.map((d, i) => ({
            ...d,
            id: `preset_dest_${i}`,
        }));
        next = { ...next, exportDestinations: [...next.exportDestinations, ...seededDests] };
    }
    // ── Pipeline stages ────────────────────────────────────────────────────────
    const templateId = presets.defaultPipelineTemplate;
    if (templateId) {
        const stages = CONTENT_STAGE_LISTS[templateId];
        if (stages && stages.length > 0) {
            next = { ...next, pipeline: { ...next.pipeline, stages: [...stages] } };
        }
    }
    return next;
}
//# sourceMappingURL=apply-category-presets.js.map